import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { Button, Card, Modal, ListGroup } from "react-bootstrap";
import { Manager, Reference, Popper } from "react-popper";
import onClickOutside from "react-onclickoutside";
import { SVGIcon } from "../../../components/Base";
import { Checkbox, Chip } from "@material-ui/core";
import moment from "moment";
import {
  DB_DATE_FORMAT,
  DB_HOURS_FORMAT,
  DISPLAY_DATE_FORMAT,
  RESERVATION_COLOR,
} from "../../../constants";

import { get } from "lodash";
import { toPesoAmount, tryParse } from "../../../helpers";
import { isReservationV2Enabled } from "../../../helpers/featureFlags";
import { getSeatingBlockedReason } from "../../../partials/reservations/seatingRules";
import core from "../../../vendors/core-api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const ACTION_LIST = [
  { icon: "profile", label: "Profile" },
  { icon: "message", label: "Message" },
  { icon: "edit_reservation", label: "Edit Reservation" },

  { icon: "add_notes", label: "Add Notes" },
];

const CUSTOMER_NOTES_TYPES = {
  MESSAGE: "has_message",
  BRINGING_SOMETHING: "is_bringing_something",
  WINE_SOMMELIER: "wine_sommelier",
  DIETARY_RESTRICTION: "has_dietary_restrictions",
  CUSTOMER_NOTES: "has_notes",
};

const CUSTOMER_NOTES = [
  { type: CUSTOMER_NOTES_TYPES.MESSAGE, icon: "message", title: "Message" },
  {
    type: CUSTOMER_NOTES_TYPES.BRINGING_SOMETHING,
    icon: "bring_something",
    title: "Bringing Something",
  },
  {
    type: CUSTOMER_NOTES_TYPES.WINE_SOMMELIER,
    icon: "wine_sommelier",
    title: "Wine Sommelier",
  },
  {
    type: CUSTOMER_NOTES_TYPES.DIETARY_RESTRICTION,
    icon: "dietary_restrictions",
    title: "Dietary Restrictions",
  },
  {
    type: CUSTOMER_NOTES_TYPES.CUSTOMER_NOTES,
    icon: "reservation_notes",
    title: "Customer Notes",
  },
];

const MySwal = withReactContent(Swal);

const dummy_task = [
  { type: CUSTOMER_NOTES_TYPES.MESSAGE, task: "Reply to message" },
  {
    type: CUSTOMER_NOTES_TYPES.DIETARY_RESTRICTION,
    task: "Print dietary restrictions / food allergies",
  },
];

const DetailsContainer = (props) => {
  const {
    customer,
    reservation_notes,
    guest_tags,
    reservation,
    onAddTags,
    onDetailsCloseClick,
    onActionButtonClick,
    onPrintBtnClick,
    onEditCelebrationClick,
    onEditDietaryClick,
    onEditReservationClick,
    onAddNotesClick,
    onStatusChange,
    onNoteUpdate,
    onNoteDelete,
    onCustomerNameClick,
  } = props;
  return (
    <div className="d-flex flex-column h-100">
      <CustomerStats
        customer={customer}
        reservation={reservation}
        onDetailsCloseClick={onDetailsCloseClick}
        onCustomerNameClick={onCustomerNameClick}
      />

      <div style={{ flex: "1 1 auto", overflowY: "auto" }}>
        <HeaderDiv
          reservation={reservation}
          onDetailsCloseClick={onDetailsCloseClick}
        />
        <ActionListContainer
          onActionButtonClick={onActionButtonClick}
          onAddNotesClick={onAddNotesClick}
          onEditReservationClick={onEditReservationClick}
        />
        <GuestTagContainer tags={guest_tags} onAddTags={onAddTags} />
        <DietaryRestrictionContainer
          onEditDietaryClick={onEditDietaryClick}
          onPrintBtnClick={onPrintBtnClick}
          reservation={reservation}
        />
        <CelebrationsContainer
          reservation={reservation}
          onEditCelebrationClick={onEditCelebrationClick}
        />

        <NotesContainer
          notes={reservation_notes}
          reservation={reservation}
          onAddNotesClick={onAddNotesClick}
          onNoteUpdate={onNoteUpdate}
          onNoteDelete={onNoteDelete}
        />
        <AdjustmentsContainer reservation={reservation} />
        <ReceiptContainer reservation={reservation} />
      </div>
      <StatusUpdateContainer
        onStatusChange={onStatusChange}
        reservation={reservation}
      />
    </div>
  );
};

let StatusMenu = ({ items, onSelect, hide }) => {
  return (
    <Card style={{ zIndex: 1051, minWidth: "200px" }} className="shadow-lg">
      <ListGroup variant="flush">
        {items.map((item) => (
          <ListGroup.Item
            action
            key={item.key}
            onClick={() => onSelect(item.key)}
          >
            <div className="d-flex align-items-center">
              <div
                className="mr-2"
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: item.color,
                }}
              />
              <span>{item.label}</span>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </Card>
  );
};

const StatusUpdateContainer = ({ onStatusChange, reservation, customer }) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const STATUSES = {
    new: {
      label: "Upcoming",
      color: "#0d6efd",
    },
    seated: {
      label: "Seated",
      color: "#3BB500",
      icon: "fa-user-check",
    },
    finished: {
      label: "Finished",
      color: "#FF6900",
      icon: "fa-flag-checkered",
    },
    cancelled: {
      label: "Mark as no show",
      color: "#F64E60",
      icon: "fa-user-times",
    },
  };

  const SELECTABLE_STATUS_KEYS = ["seated", "finished", "cancelled"];
  const statusMap = {
    new: "upcoming",
    accepted: "seated",
    completed: "finished",
    cancelled: "cancelled",
  };
  const converted_status = statusMap[reservation.status] || "upcoming";
  const currentStatus = STATUSES[converted_status] || STATUSES.new;

  const handleToggleClick = () => setMenuOpen(!isMenuOpen);
  const handleSelect = (statusKey) => {
    setMenuOpen(false);

    if (statusKey === "seated" || statusKey === "finished") {
      const reason = getSeatingBlockedReason(reservation, statusKey);
      if (reason) {
        const isSeated = statusKey === "seated";
        MySwal.fire({
          title: isSeated
            ? "Cannot Seat Reservation"
            : "Cannot Finish Reservation",
          text: reason,
          icon: "warning",
          confirmButtonColor: RESERVATION_COLOR,
        });
        return;
      }
    }

    onStatusChange(statusKey);
  };

  const menuItems = SELECTABLE_STATUS_KEYS.filter(
    (key) => key !== converted_status,
  ).map((key) => ({
    key,
    ...STATUSES[key],
  }));

  return (
    <div className="p-3">
      <Manager>
        <Reference>
          {({ ref }) => (
            <button
              ref={ref}
              onClick={handleToggleClick}
              className="d-flex align-items-center justify-content-between btn btn-light border"
              style={{
                textAlign: "left",
                backgroundColor: "white",
                width: "25%",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: currentStatus.color,
                    marginRight: "10px",
                  }}
                />
                <span>{currentStatus.label}</span>
              </div>
              <span
                style={{ fontSize: "1.5rem", color: "#6c757d", lineHeight: 1 }}
              >
                ›
              </span>
            </button>
          )}
        </Reference>
        {isMenuOpen && (
          <Popper placement="bottom-start">
            {({ ref, style, placement }) => (
              <div ref={ref} style={style} data-placement={placement}>
                <StatusMenu
                  items={menuItems}
                  onSelect={handleSelect}
                  hide={() => setMenuOpen(false)}
                />
              </div>
            )}
          </Popper>
        )}
      </Manager>
    </div>
  );
};

const formatPreviousReservationDate = (orderDate) => {
  if (!orderDate) return "";
  try {
    const m = moment(orderDate);
    if (m.isValid()) {
      return m.format("MMM DD, YYYY");
    }
  } catch (e) {
    // fallback
  }
  return orderDate;
};

const NotesContainer = (props) => {
  const {
    notes: reservationNotes,
    reservation,
    onAddNotesClick,
    onNoteUpdate,
    onNoteDelete,
  } = props;

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState("");
  const [showAllNotes, setShowAllNotes] = useState(false);
  const editInputRef = useRef(null);
  const isCancellingRef = useRef(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (editingNoteId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingNoteId]);

  useEffect(() => {
    setShowAllNotes(false);
    setEditingNoteId(null);
    setEditedNoteText("");
  }, [reservation?._id]);

  const sortedNotes = useMemo(() => {
    if (!Array.isArray(reservationNotes)) return [];
    return [...reservationNotes].sort(
      (a, b) => new Date(b.date_created) - new Date(a.date_created),
    );
  }, [reservationNotes]);

  const notesToShow = showAllNotes ? sortedNotes : sortedNotes.slice(0, 4);

  const handleEditClick = (note) => {
    isCancellingRef.current = false;
    isSavingRef.current = false;
    setEditingNoteId(note._id);
    setEditedNoteText(note.note || note.notes || "");
  };

  const handleSaveEdit = (note) => {
    if (isCancellingRef.current) {
      isCancellingRef.current = false;
      return;
    }
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    const orderId = note?.order_id || reservation?._id;
    if (editedNoteText.trim() === "") {
      onNoteDelete(orderId, note?._id);
    } else {
      onNoteUpdate(orderId, note?._id, editedNoteText.trim());
    }
    setEditingNoteId(null);
    setEditedNoteText("");
  };

  const handleCancelEdit = () => {
    isCancellingRef.current = true;
    isSavingRef.current = false;
    setEditingNoteId(null);
    setEditedNoteText("");
  };

  return (
    <Card
      className="m-5 mb-3"
      style={{
        border: "1.5px solid #000000",
        borderRadius: 4,
      }}
    >
      <Card.Body className="d-flex flex-column p-0 mb-3">
        <div className="d-flex flex-row justify-content-between px-2 pt-1">
          <div className="d-flex align-items-center">
            <i className="fas fa-sticky-note" style={{ color: "#464E5F" }} />
            <span className="p-1 font-weight-bolder">Notes</span>
          </div>
          <div className="d-flex align-items-center">
            <a
              style={{ color: "#464e5f", textDecoration: "underline" }}
              onClick={(e) => {
                e.preventDefault();
                onAddNotesClick();
              }}
              href="#"
            >
              + Add
            </a>
          </div>
        </div>

        {!Array.isArray(sortedNotes) || sortedNotes.length === 0 ? (
          <div className="px-3 pt-2 text-muted" style={{ fontSize: "0.85rem" }}>
            None
          </div>
        ) : (
          <div
            className="d-flex flex-column mx-2 p-3 mt-2"
            style={{
              backgroundColor: "#eef0f6",
              borderRadius: 4,
            }}
          >
            {notesToShow.map((data, index) => {
              const isPrevious =
                typeof data.is_previous === "boolean"
                  ? data.is_previous
                  : Boolean(
                      data.order_id &&
                        reservation?._id &&
                        String(data.order_id) !== String(reservation._id),
                    );

              return (
                <div
                  key={data._id || `note-${index}`}
                  className="d-flex flex-row mb-2"
                  style={{
                    backgroundColor: isPrevious ? "#FFFDF7" : "#FFFFFF",
                    border: isPrevious
                      ? "1px solid #FDE68A"
                      : "1px solid #EBEDF3",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  {/* Left Highlight Strip */}
                  <div
                    style={{
                      width: 5,
                      minWidth: 5,
                      backgroundColor: isPrevious
                        ? "#F59E0B"
                        : RESERVATION_COLOR,
                    }}
                  />

                  {/* Note Body */}
                  <div className="d-flex flex-column flex-grow-1 p-3">
                    {/* Previous Reservation Badge */}
                    {isPrevious && (
                      <div
                        className="d-inline-flex align-items-center mb-2 px-2 py-1"
                        style={{
                          backgroundColor: "#FEF3C7",
                          border: "1px solid #FDE68A",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#92400E",
                          alignSelf: "flex-start",
                          lineHeight: 1.2,
                        }}
                      >
                        <i
                          className="fas fa-history mr-1"
                          style={{ fontSize: "11px", color: "#92400E" }}
                        />
                        <span>
                          Previous Reservation
                          {data.order_date
                            ? ` • ${formatPreviousReservationDate(
                                data.order_date,
                              )}`
                            : ""}
                        </span>
                      </div>
                    )}

                    {/* Content or Input */}
                    {editingNoteId === data._id ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editedNoteText}
                        onChange={(e) => setEditedNoteText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(data);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        onBlur={() => handleSaveEdit(data)}
                        className="form-control form-control-sm my-1"
                        style={{ fontSize: "0.85rem" }}
                      />
                    ) : (
                      <div
                        className="py-1 cursor-pointer"
                        style={{
                          color: "#3F4254",
                          fontSize: "0.85rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.4,
                        }}
                        onClick={() => handleEditClick(data)}
                      >
                        {data.note || data.notes}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="d-flex align-items-center justify-content-between pt-2">
                      <span style={{ color: "#7E8299", fontSize: "0.75rem" }}>
                        {data.created_by} on{" "}
                        {moment(data.date_created).format(
                          "MMM DD, YYYY [at] h:mm A",
                        )}
                      </span>

                      <div className="d-flex align-items-center">
                        {editingNoteId !== data._id ? (
                          <>
                            <i
                              className="far fa-edit text-primary mr-3 cursor-pointer"
                              title="Edit Note"
                              style={{ fontSize: "0.85rem" }}
                              onClick={() => handleEditClick(data)}
                            />
                            <i
                              className="far fa-trash-alt text-danger cursor-pointer"
                              title="Delete Note"
                              style={{ fontSize: "0.85rem" }}
                              onClick={() =>
                                onNoteDelete(
                                  data.order_id || reservation?._id,
                                  data._id,
                                )
                              }
                            />
                          </>
                        ) : (
                          <>
                            <i
                              className="fas fa-check text-success mr-3 cursor-pointer"
                              title="Save"
                              style={{ fontSize: "0.9rem" }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSaveEdit(data)}
                            />
                            <i
                              className="fas fa-times text-danger cursor-pointer"
                              title="Cancel"
                              style={{ fontSize: "0.9rem" }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleCancelEdit();
                              }}
                              onClick={handleCancelEdit}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination / View More */}
            {sortedNotes.length > 4 && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-link font-weight-bold p-0"
                  style={{
                    color: RESERVATION_COLOR,
                    fontSize: "0.85rem",
                    textDecoration: "none",
                    boxShadow: "none",
                  }}
                  onClick={() => setShowAllNotes(!showAllNotes)}
                >
                  {showAllNotes
                    ? "View Less"
                    : `View ${sortedNotes.length - 4} More...`}
                </button>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const celebrationIcons = {
  Birthday: "fa-birthday-cake",
  Anniversary: "fa-heart",
  Graduation: "fa-graduation-cap",
  Engagement: "fa-ring",
  Reunion: "fa-users",
  Promotion: "fa-chart-line",
  "Baby Shower": "fa-baby",
  "Bridal Shower": "fa-female",
  Wedding: "fa-church",
};

const allergyIcons = {
  Vegetarian: "fa-leaf",
  Vegan: "fa-seedling",
};

const CelebrationsContainer = (props) => {
  const { reservation, onEditCelebrationClick } = props;
  const { notes } = reservation || {};
  const celebration = get(tryParse(notes), "celebration", []);
  return (
    <Card
      className="mx-5 mt-3"
      style={{
        border: "1.5px dashed #000000",
        borderRadius: 4,
      }}
    >
      <Card.Body className="d-flex flex-column p-0 mb-3">
        <div className="d-flex flex-row justify-content-space-between px-2 pt-1">
          <div className="d-flex align-items-center">
            <i className="fas fa-gift" style={{ color: "#464E5F" }} />
            <span className="p-1 font-weight-bolder">Celebrations</span>
          </div>
          <div className="d-flex align-items-center">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onEditCelebrationClick();
              }}
              style={{ color: "#464e5f", textDecoration: "underline" }}
            >
              + Add
            </a>
          </div>
        </div>
        {celebration.length > 0 && (
          <div
            className="d-flex flex-column mx-2 p-2"
            style={{
              maxWidth: 465,
              backgroundColor: "#EEF0F6",
              borderRadius: 4,
            }}
          >
            <div className="d-flex flex-wrap">
              {celebration.map((item, index) => {
                return (
                  <div
                    className="d-flex m-1"
                    key={`celebration-${item.label}-${index}`}
                  >
                    <Chip
                      style={{ backgroundColor: "#EEE5FF", color: "#8950FC" }}
                      size="small"
                      label={
                        item.count > 1
                          ? `${item.label} (x${item.count})`
                          : item.label
                      }
                      icon={
                        <i
                          className={`fa fa-sm ${
                            celebrationIcons[item.label] || "fa-gift"
                          }`}
                          style={{ color: "#8950FC", padding: "0.35rem" }}
                        />
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const DietaryRestrictionContainer = (props) => {
  const { isModal, onPrintBtnClick, onEditDietaryClick, reservation } = props;
  const { notes } = reservation || {};
  const dietary = get(tryParse(notes), "dietary", []);

  return (
    <Card
      className="mx-5 mt-0"
      style={{
        border: "1.5px dashed #000000",
        borderRadius: 4,
      }}
    >
      <Card.Body className="d-flex flex-column p-0 mb-3">
        <div className="d-flex flex-row justify-content-space-between px-2 pt-1">
          <div className="d-flex align-items-center">
            <i
              className="fas fa-exclamation-circle"
              style={{ color: "#464E5F" }}
            />
            <span className="p-1 font-weight-bolder">
              Dietary Preferences / Food Allergies
            </span>
          </div>

          <div className="d-flex align-items-center">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onEditDietaryClick();
              }}
              style={{ color: "#464e5f", textDecoration: "underline" }}
            >
              + Add
            </a>
          </div>
        </div>
        {dietary.length > 0 && (
          <div
            className="d-flex flex-column mx-2 p-2"
            style={{
              maxWidth: 465,
              backgroundColor: "#EEF0F6",
              borderRadius: 4,
            }}
          >
            <div className="d-flex flex-wrap">
              {dietary.map((item, index) => {
                return (
                  <div
                    className="d-flex m-1"
                    key={`restriction-${item.label}-${index}`}
                  >
                    <Chip
                      style={{ backgroundColor: "#FFD2D2", color: "#D90429" }}
                      size="small"
                      label={
                        item.count > 1
                          ? `${item.label} (x${item.count})`
                          : item.label
                      }
                      icon={
                        <i
                          className={`fa fa-sm ${
                            allergyIcons[item.label] ||
                            "fa-exclamation-triangle"
                          }`}
                          style={{
                            color: "#D90429",
                            padding: "0.35rem",
                          }}
                        />
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const AddTagContainer = ({ tags, onSaveClick }) => {
  const [newTags, setNewTags] = useState(tags || []);
  const [currentTag, setCurrentTag] = useState("");

  useEffect(() => {}, [newTags]);

  const handleAddTag = () => {
    const newTagValue = currentTag.trim();
    if (newTagValue && !newTags.includes(newTagValue)) {
      setNewTags([...newTags, newTagValue]);
      setCurrentTag(""); // Clear input after adding
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="d-flex flex-column p-2">
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          name="addTag"
          placeholder="Enter a new tag or click Add"
          value={currentTag}
          onChange={(e) => setCurrentTag(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ width: "100%" }}
        />
        <div className="input-group-append">
          <Button variant="info" onClick={handleAddTag}>
            Add
          </Button>
        </div>
      </div>
      <div className="d-flex mt-2 mx-1" style={{ color: "#989898" }}>
        Or delete existing tags:
      </div>
      <Card>
        <Card.Body className="p-3">
          <div className="d-flex flex-wrap">
            {newTags.map((label, i) => {
              return (
                <div className="d-flex p-1" key={`tag-${label}-00${i}`}>
                  <Chip
                    style={{ backgroundColor: "#FF7F00", color: "#fff" }}
                    size="small"
                    label={label}
                    onDelete={() => {
                      const filteredTags = newTags.filter((t) => t !== label);
                      setNewTags(filteredTags);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
      <div className="d-flex justify-content-center p-3">
        <button
          type="submit"
          className="btn btn-info"
          style={{ width: "50%" }}
          onClick={(e) => onSaveClick(e, newTags)}
        >
          Done
        </button>
      </div>
    </div>
  );
};

const GuestTagContainer = (props) => {
  const { tags, onAddTags } = props;
  const [showModal, setShowModal] = useState(false);

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  return (
    <>
      <Card
        className="m-5"
        style={{
          border: "1.5px solid #000000",
          borderRadius: 4,
        }}
      >
        <Card.Body className="d-flex flex-column p-0 mb-3">
          <div className="d-flex flex-row justify-content-space-between px-2 pt-1">
            <div className="d-flex align-items-center">
              <i className="fas fa-tags" style={{ color: "#464E5F" }} />
              <span className="p-1 font-weight-bolder">Guest Tags</span>
            </div>

            <div className="d-flex align-items-center">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleShow();
                }}
                style={{ color: "#464e5f", textDecoration: "underline" }}
              >
                + Add
              </a>
            </div>
          </div>
          <div className="d-flex flex-wrap mx-1" style={{ maxWidth: 450 }}>
            {tags &&
              tags.length > 0 &&
              tags.map((label, i) => {
                return (
                  <div className="d-flex p-1" key={`tag-${label}-00${i}`}>
                    <Chip
                      style={{ backgroundColor: "#8950FC", color: "#fff" }}
                      size="small"
                      label={label}
                    />
                  </div>
                );
              })}
          </div>
        </Card.Body>
      </Card>
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Guest Tags</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddTagContainer
            tags={tags}
            onSaveClick={(e, values) => {
              onAddTags(e, values);
              handleClose();
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

const TaskContainer = (props) => {
  return (
    <Card className="b-none px-5">
      <Card.Title className="py-0 mb-2">Tasks</Card.Title>
      {dummy_task.map(({ type, task }, i) => {
        const taskIcon = CUSTOMER_NOTES.filter(
          (notes) => notes.type === type,
        )[0];
        return (
          <div className="p-0" key={`reservation-task-0${i + 1}`}>
            <Card.Body className="d-flex flex-row align-items-center p-0">
              <Checkbox className="pl-0 py-2 pr-2" color="default" />
              <TaskRow className="d-flex flex-row py-1 px-3">
                <SVGIcon
                  icon={`Reservations/${taskIcon.icon}`}
                  variant="dark"
                />
                <span className="ml-2 font-pickup-sm">{task}</span>
              </TaskRow>
            </Card.Body>
          </div>
        );
      })}
    </Card>
  );
};

const ActionListContainer = (props) => {
  const { onAddNotesClick, onActionButtonClick, onEditReservationClick } =
    props;
  return (
    <div className="d-flex flex-row justify-content-space-between px-48 p-5 mt-3">
      {ACTION_LIST.map(({ icon, label }, index) => {
        const isDisabled = label === "Message";
        const onClickAction = () => {
          if (isDisabled) return;
          if (label === "Add Notes") {
            onAddNotesClick();
          } else if (label === "Edit Reservation") {
            onEditReservationClick();
          } else {
            onActionButtonClick(icon);
          }
        };
        return (
          <ActionListBox
            key={`action-btn-${icon}`}
            className="py-1 px-0"
            onClick={onClickAction}
            disabled={isDisabled}
            style={{
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            <SVGIcon
              icon={`Reservations/action_${icon}`}
              fill="#464E5F"
              variant="dark"
            />
            <span style={{ fontSize: "0.75rem" }}>{label}</span>
          </ActionListBox>
        );
      })}
    </div>
  );
};

const HeaderDiv = (props) => {
  const { reservation } = props;
  const {
    guest_customer = {},
    order_details = [],
    order_time = "",
    order_date,
    status,
    date_updated = new Date(),
  } = reservation;
  const experience = order_details.find(
    (detail) => detail.item.type === "reservation_experience",
  );
  const table = order_details.find(
    (detail) => detail.item.type === "reservation",
  );
  const experiences = order_details.filter(
    (detail) => detail.item.type === "reservation_experience",
  );
  const guest_count = experiences.reduce((acc, cur) => acc + cur.qty, 0);
  const end_time = status !== "completed" ? new Date() : date_updated;

  const format_end_time = moment(end_time, DB_HOURS_FORMAT);
  const format_order_time = moment(order_time, DB_HOURS_FORMAT);
  const format_order_date = moment(order_date, DB_DATE_FORMAT).format(
    DISPLAY_DATE_FORMAT,
  );

  const time_consumed = moment.duration(
    format_end_time.diff(format_order_time),
  );

  const duration_display = `${moment(order_time, DB_HOURS_FORMAT).format(
    "hh:mmA",
  )}`;

  return (
    <div
      className="d-flex flex-column p-3 mx-5"
      style={{ background: "#eef0f6", borderRadius: "15px" }}
    >
      <div className="d-flex flex-row justify-content-space-between align-items-center px-1 w-100">
        <div className="d-flex flex-row align-items-center font-card-title font-weight-bold text-capitalize">
          <div
            style={{
              maxWidth: 350,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {format_order_date}
          </div>
        </div>
      </div>
      <HeaderDivider />
      <div className="d-flex flex-row p-2 font-pickup-sm font-weight-bold">
        <div className="d-flex mr-3">
          <SVGIcon
            className="mr-2"
            icon="Reservations/reservation-icon"
            fill="#464E5F"
            variant="dark"
          />
          {experience?.item?.name}
        </div>
        <div className="d-flex mr-3">
          <SVGIcon
            className="mr-2"
            icon="Reservations/table-black"
            fill="#464E5F"
            variant="dark"
          />
          {table?.item?.name}
        </div>
        <div className="d-flex mr-3">
          <SVGIcon
            className="mr-2"
            icon="Reservations/guest"
            fill="#464E5F"
            variant="dark"
          />
          <span>{guest_count || 0} guests</span>
        </div>

        <div className="d-flex">
          <SVGIcon
            className="mr-2"
            icon="Reservations/time"
            fill="#464E5F"
            variant="dark"
          />
          <span>{duration_display}</span>
        </div>
      </div>
      <ContactInformation
        guest_customer={guest_customer}
        partner_identifier={reservation.partner_identifier}
      />
    </div>
  );
};

const ContactInformation = (props) => {
  const { guest_customer, partner_identifier } = props;
  return (
    <div className="d-flex flex-column mt-3">
      <div className="d-flex px-2 font-weight-bolder">Contact Information</div>
      <div className="d-flex align-items-center px-2">
        <i className="flaticon2-email mr-2" style={{ color: "#464E5F" }} />
        <span style={{ textDecoration: "underline" }}>
          {guest_customer?.email || partner_identifier?.email}
        </span>
      </div>
      <div className="d-flex justify-content-space-between flex-row px-2">
        <div className="d-flex align-items-center">
          <i className="fas fa-phone mr-2" style={{ color: "#464E5F" }} />
          <span style={{ textDecoration: "underline" }}>
            {guest_customer?.contact_number || partner_identifier?.phone}
          </span>
        </div>
      </div>
    </div>
  );
};

const TaskRow = styled.div`
  background-color: #d8e2ff;
  width: 100%;
  border-radius: 4px;
`;

const ActionListBox = styled.button`
  display: flex;
  flex-direction: column;
  border: 1px solid #464e5f;
  border-radius: 4px;
  width: 85px;
  height: 40px;
  align-items: center;
  background: #fff;
`;

const HeaderDivider = styled.div`
  background: #eef0f6;
  border: 0.5px solid #484848;
`;

const CustomerStats = ({
  reservation,
  customer,
  onDetailsCloseClick,
  onCustomerNameClick,
}) => {
  const [stats, setStats] = useState({
    total_visits: 0,
    reservations: 0,
    events: 0,
    no_shows: 0,
    walk_ins: 0,
    cancellations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isVip, setIsVip] = useState(customer?.level === "VIP" || false);
  const api = core("pu").get();

  useEffect(() => {
    const fetchStats = async () => {
      if (reservation && reservation.partner_identifier) {
        setLoading(true);
        try {
          const { data } = await api.get(
            `/orders/reservation-stats-by-identifier?partner_identifier=${reservation.partner_identifier._id}`,
          );
          setStats(data);
        } catch (error) {
          console.error("Failed to fetch customer stats:", error);
          // Optionally, set stats to a default error state or keep them as 0s
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false); // No identifier, no stats to fetch
      }
    };

    fetchStats();
    setIsVip(customer?.level === "VIP" || false);
  }, [reservation, customer]);

  const handleVipToggle = async () => {
    if (!customer || !customer._id) return;

    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `You are about to ${
        isVip ? "remove VIP status from" : "add VIP status to"
      } this customer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: RESERVATION_COLOR,
      cancelButtonColor: "#F64E60",
      confirmButtonText: "Yes, proceed!",
    });

    if (result.isConfirmed) {
      try {
        await api.put({
          url: `/customer/${customer._id}/level/vip`,
          data: { is_vip: !isVip },
        });
        setIsVip(!isVip);
        MySwal.fire({
          toast: true,
          icon: "success",
          title: "VIP status updated!",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        MySwal.fire("Error!", "Could not update VIP status.", "error");
      }
    }
  };
  const {
    total_visits,
    reservations,
    events,
    no_shows,
    walk_ins,
    cancellations,
    revenue,
  } = stats;
  const { guest_customer = {} } = reservation;

  const customer_name = `${guest_customer.last_name}, ${guest_customer.first_name}`;

  return (
    <div className="py-4 px-6" style={{ fontSize: "0.8rem" }}>
      <div className="d-flex flex-row justify-content-space-between align-items-center px-1 w-100">
        <div className="d-flex flex-row align-items-center font-card-title font-weight-bold text-capitalize">
          <div
            onClick={() => {
              onCustomerNameClick(customer);
            }}
            style={{
              maxWidth: 350,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {customer_name}
          </div>
          <i
            className="fas fa-crown ml-2"
            style={{
              color: isVip ? "#FFD700" : "#BDBDBD",
              cursor: "pointer",
            }}
            onClick={handleVipToggle}
          />
          {reservation?.reservation_notes?.custom_amount !== undefined && (
            <Chip
              size="small"
              label="Custom price"
              style={{
                marginLeft: 8,
                backgroundColor: RESERVATION_COLOR,
                color: "#fff",
              }}
            />
          )}
          {reservation?.reservation_notes?.party_size_override && (
            <Chip
              size="small"
              label="Pax override"
              style={{
                marginLeft: 8,
                backgroundColor: "#FFA800",
                color: "#fff",
              }}
            />
          )}
        </div>
        <Button
          className=" text-black"
          variant="outline"
          type="button"
          size="sm"
          onClick={onDetailsCloseClick}
        >
          <i className="flaticon2-cross res-close-icon" />
        </Button>
      </div>
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "80px" }}
        >
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          <span className="ml-2">Loading Stats...</span>
        </div>
      ) : (
        <StatsTable>
          <tbody>
            <tr>
              <TotalVisitsCell rowSpan={2}>
                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                  <div style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                    TOTAL VISITS
                  </div>
                  <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                    {total_visits}
                  </div>
                </div>
              </TotalVisitsCell>
              <StatsValueCell>RESERVATIONS</StatsValueCell>
              <StatsValue>{reservations}</StatsValue>
              <StatsValueCell>EVENTS</StatsValueCell>
              <StatsValue>{events}</StatsValue>
              <StatsValueCell>NO SHOWS</StatsValueCell>
              <StatsValue>{no_shows}</StatsValue>
            </tr>
            <tr>
              <StatsValueCell>WALK-INS</StatsValueCell>
              <StatsValue>{walk_ins}</StatsValue>
              <StatsValueCell>CANCELLATIONS</StatsValueCell>
              <StatsValue>{cancellations}</StatsValue>
              <StatsValueCell>Revenue</StatsValueCell>
              <StatsValue>{toPesoAmount(revenue || 0)}</StatsValue>
            </tr>
          </tbody>
        </StatsTable>
      )}
    </div>
  );
};

// Styled components for the stats table
const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  &,
  th,
  td {
    border: 1px solid #000;
  }
`;

const TotalVisitsCell = styled.td`
  width: 25%;
  height: 80px; /* Adjusted height to accommodate 2 rows */
  text-align: center;
  font-size: 0.8rem;
  padding: 0;
  vertical-align: middle;
`;

const StatsValueCell = styled.td`
  width: 13%; /* Adjusted width for 4 cells in the second part */
  text-align: left;
  font-weight: bold;
  padding: 0.5rem;
  font-size: 1rem;
`;

const StatsValue = styled.td`
  width: 13%; /* Adjusted width for 4 cells in the second part */
  text-align: center;
  font-weight: bold;
  padding: 0.5rem;
  font-size: 1rem;
`;

const AdjustmentsContainer = ({ reservation }) => {
  const currency = "Php";
  const rawNotes = get(reservation, "reservation_notes", {});
  const parsedNotes =
    typeof rawNotes === "string" ? tryParse(rawNotes) || {} : rawNotes || {};
  const adjustments = parsedNotes.adjustments || {};

  const expAdj = Array.isArray(adjustments.experience)
    ? adjustments.experience[adjustments.experience.length - 1]
    : adjustments.experience;

  const tableAdj = Array.isArray(adjustments.table)
    ? adjustments.table[adjustments.table.length - 1]
    : adjustments.table;

  const partyAdj = Array.isArray(adjustments.party_size)
    ? adjustments.party_size.reduce(
        (acc, curr) => ({
          ...curr,
          qty: (acc.qty || 0) + (curr.qty || 0),
          price: (acc.price || 0) + (curr.price || 0),
        }),
        {},
      )
    : adjustments.party_size;

  const hasAdjustments = Boolean(
    (expAdj && (expAdj.from || expAdj.to || expAdj.price !== undefined)) ||
      (tableAdj && (tableAdj.from || tableAdj.to)) ||
      (partyAdj &&
        (partyAdj.qty !== undefined ||
          partyAdj.from ||
          partyAdj.to ||
          partyAdj.price !== undefined)),
  );

  if (!hasAdjustments) {
    return null;
  }

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const m = Number(ts) > 1e11 ? moment(Number(ts)) : moment.unix(Number(ts));
    return m.isValid() ? m.format("MMM D, h:mm A") : "";
  };

  const getApproverName = (approver) => {
    if (!approver) return "";
    if (typeof approver === "object") return approver.name || "";
    return String(approver);
  };

  return (
    <Card
      className="m-4"
      style={{
        border: "1px solid #EBEDF3",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <Card.Body className="p-4">
        {/* Header Section */}
        <div className="d-flex align-items-center mb-4">
          <i
            className="fas fa-sliders-h"
            style={{
              color: "#3F4254",
              fontSize: "1.2rem",
              marginRight: "0.75rem",
            }}
          />
          <span
            className="h5 mb-0 font-weight-bolder"
            style={{ color: "#3F4254" }}
          >
            Adjustments
          </span>
        </div>

        {/* Gray Box for Adjustment Items */}
        <div
          className="p-4 flex-grow-1"
          style={{
            backgroundColor: "#F5F8FA",
            borderRadius: "6px",
          }}
        >
          {/* Experience Change */}
          {expAdj &&
            (expAdj.from || expAdj.to || expAdj.price !== undefined) && (
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    Experience Change
                  </span>
                  {(expAdj.from || expAdj.to) && (
                    <>
                      <br />
                      <small style={{ color: "#B5B5C3" }}>
                        {`From: ${expAdj.from || ""} -> To: ${expAdj.to || ""}`}
                      </small>
                    </>
                  )}
                  {(expAdj.approver || expAdj.timestamp) && (
                    <>
                      <br />
                      <small style={{ color: "#B5B5C3" }}>
                        {getApproverName(expAdj.approver)
                          ? `By: ${getApproverName(expAdj.approver)}`
                          : ""}
                        {expAdj.timestamp
                          ? ` on ${formatTimestamp(expAdj.timestamp)}`
                          : ""}
                      </small>
                    </>
                  )}
                </div>
                {expAdj.price !== undefined && (
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    {toPesoAmount(expAdj.price, currency).replace(".00", "")}
                  </span>
                )}
              </div>
            )}

          {/* Table Change */}
          {tableAdj && (tableAdj.from || tableAdj.to) && (
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span style={{ color: "#3F4254", fontWeight: "500" }}>
                  Table Change
                </span>
                <br />
                <small style={{ color: "#B5B5C3" }}>
                  {`From: ${tableAdj.from || ""} -> To: ${tableAdj.to || ""}`}
                </small>
                {(tableAdj.approver || tableAdj.timestamp) && (
                  <>
                    <br />
                    <small style={{ color: "#B5B5C3" }}>
                      {getApproverName(tableAdj.approver)
                        ? `By: ${getApproverName(tableAdj.approver)}`
                        : ""}
                      {tableAdj.timestamp
                        ? ` on ${formatTimestamp(tableAdj.timestamp)}`
                        : ""}
                    </small>
                  </>
                )}
              </div>
              {tableAdj.price !== undefined && (
                <span style={{ color: "#3F4254", fontWeight: "500" }}>
                  {toPesoAmount(tableAdj.price, currency).replace(".00", "")}
                </span>
              )}
            </div>
          )}

          {/* Party Size Change */}
          {partyAdj &&
            (partyAdj.qty !== undefined ||
              partyAdj.from ||
              partyAdj.to ||
              partyAdj.price !== undefined) && (
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    Party Size Change
                  </span>
                  <br />
                  <small style={{ color: "#B5B5C3" }}>
                    {partyAdj.qty !== undefined
                      ? `Added: ${partyAdj.qty} guest(s)`
                      : partyAdj.from
                      ? `From: ${partyAdj.from} -> To: ${partyAdj.to}`
                      : ""}
                  </small>
                  {(partyAdj.approver || partyAdj.timestamp) && (
                    <>
                      <br />
                      <small style={{ color: "#B5B5C3" }}>
                        {getApproverName(partyAdj.approver)
                          ? `By: ${getApproverName(partyAdj.approver)}`
                          : ""}
                        {partyAdj.timestamp
                          ? ` on ${formatTimestamp(partyAdj.timestamp)}`
                          : ""}
                      </small>
                    </>
                  )}
                </div>
                {partyAdj.price !== undefined && (
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    {toPesoAmount(partyAdj.price, currency).replace(".00", "")}
                  </span>
                )}
              </div>
            )}
        </div>
      </Card.Body>
    </Card>
  );
};

const ReceiptContainer = ({ reservation, isV2: isV2Prop }) => {
  const currency = "Php"; // Assuming currency is always Php
  const order_details = get(reservation, "order_details", []);
  const isPending =
    reservation?.payment?.status === "pending" ||
    reservation?.payment?.status === "failed";
  const isPaidStatus = reservation?.payment?.status === "paid";
  const rawPaid = Number(get(reservation, "payment.amount", 0));
  const paidAmount =
    isPending || !isPaidStatus
      ? 0
      : !isNaN(rawPaid) && rawPaid > 0
      ? rawPaid
      : 0;

  const experiences = order_details.filter(
    (detail) => detail.item?.type === "reservation_experience",
  );

  const additionals = order_details.filter(
    (detail) => detail.item?.type === "reservation_addons",
  );

  const experienceSubtotal = experiences.reduce(
    (acc, item) => acc + (item.price || 0) * (item.qty || 0),
    0,
  );

  const additionalsSubtotal = additionals.reduce(
    (acc, item) => acc + (item.price || 0) * (item.qty ?? 1),
    0,
  );

  const rawNotes = get(reservation, "reservation_notes", {});
  const parsedNotes =
    typeof rawNotes === "string" ? tryParse(rawNotes) || {} : rawNotes || {};
  const adjustments = parsedNotes.adjustments || {};

  const adjustmentsSubtotal = Object.values(adjustments).reduce(
    (acc, item) =>
      acc +
      (Array.isArray(item)
        ? item.reduce((sum, curr) => sum + (Number(curr?.price) || 0), 0)
        : Number(item?.price) || 0),
    0,
  );

  const subtotal = experienceSubtotal + additionalsSubtotal;
  const isV2 = isV2Prop !== undefined ? isV2Prop : isReservationV2Enabled();

  // In v2: An admin-set custom price replaces the calculated total outright;
  // otherwise total includes adjustments.
  // In v1: Subtotal is used directly as the total.
  // In both: unpaid amount is total - paid (clamped at 0).
  const customAmount =
    parsedNotes.custom_amount ??
    get(reservation, "reservation_notes.custom_amount");
  const total = isV2
    ? typeof customAmount === "number"
      ? customAmount
      : subtotal + adjustmentsSubtotal
    : subtotal;

  const unpaidAmount = Math.max(0, total - paidAmount);

  let remainingPaidForAdditionals = Math.max(
    0,
    paidAmount - experienceSubtotal,
  );
  const additionalsWithPaidStatus = additionals.map((order_detail) => {
    const itemAmount = (order_detail.qty ?? 1) * (order_detail.price || 0);
    if (
      paidAmount > 0 &&
      remainingPaidForAdditionals >= itemAmount &&
      itemAmount > 0
    ) {
      remainingPaidForAdditionals -= itemAmount;
      return { ...order_detail, isPaidItem: true };
    }
    return { ...order_detail, isPaidItem: false };
  });

  return (
    <Card
      className="m-4"
      style={{
        border: "1px solid #EBEDF3",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <Card.Body className="p-4">
        {/* Header Section */}
        <div className="d-flex align-items-center mb-4">
          <i
            className="fas fa-receipt" // Assuming Font Awesome is available
            style={{
              color: "#3F4254",
              fontSize: "1.2rem",
              marginRight: "0.75rem",
            }}
          />
          <span
            className="h5 mb-0 font-weight-bolder"
            style={{ color: "#3F4254" }}
          >
            Receipt
          </span>
        </div>

        {/* Receipt Details Wrapper */}
        <div className="d-flex align-items-end">
          {/* Gray Box for Receipt Items */}
          <div
            className="p-4 flex-grow-1"
            style={{
              backgroundColor: "#F5F8FA",
              borderRadius: "6px",
            }}
          >
            {/* Main Experience Item(s) */}
            {experiences.map((order_detail, index) => (
              <div
                key={`exp-${index}`}
                className="d-flex justify-content-between align-items-start mb-2"
              >
                <div>
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    {order_detail.item?.name}
                  </span>
                  <br />
                  <small style={{ color: "#B5B5C3" }}>
                    {order_detail.qty} x{" "}
                    {toPesoAmount(order_detail.price || 0, currency).replace(
                      ".00",
                      "",
                    )}
                  </small>
                </div>
                <span style={{ color: "#3F4254", fontWeight: "500" }}>
                  {toPesoAmount(
                    (order_detail.qty || 0) * (order_detail.price || 0),
                    currency,
                  ).replace(".00", "")}
                </span>
              </div>
            ))}

            {/* Additionals Item(s) */}
            {additionalsWithPaidStatus.map((order_detail, index) => (
              <div
                key={`addon-${index}`}
                className="d-flex justify-content-between align-items-start mb-2"
              >
                <div>
                  <span style={{ color: "#3F4254", fontWeight: "500" }}>
                    {order_detail.item?.name || order_detail.name}
                  </span>
                  <br />
                  <small style={{ color: "#B5B5C3" }}>
                    {order_detail.qty ?? 1} x{" "}
                    {toPesoAmount(order_detail.price || 0, currency).replace(
                      ".00",
                      "",
                    )}
                  </small>
                </div>
                <span style={{ color: "#3F4254", fontWeight: "500" }}>
                  {toPesoAmount(
                    (order_detail.qty ?? 1) * (order_detail.price || 0),
                    currency,
                  ).replace(".00", "")}
                </span>
              </div>
            ))}

            <hr
              style={{ margin: "1rem 0", borderTop: "1.5px solid #EBEDF3" }}
            />

            {/* Subtotal */}
            <div className="d-flex justify-content-between mb-2">
              <span style={{ color: "#7E8299" }}>Subtotal</span>
              <span style={{ color: "#3F4254", fontWeight: "500" }}>
                {toPesoAmount(subtotal, currency).replace(".00", "")}
              </span>
            </div>

            <hr
              style={{ margin: "1rem 0", borderTop: "1.5px solid #EBEDF3" }}
            />

            {/* Total */}
            <div className="d-flex justify-content-between mb-2">
              <span className="font-weight-bolder" style={{ color: "#3F4254" }}>
                TOTAL
              </span>
              <span className="font-weight-bolder" style={{ color: "#3F4254" }}>
                {toPesoAmount(total, currency).replace(".00", "")}
              </span>
            </div>

            {/* Paid */}
            <div className="d-flex justify-content-between mb-2">
              <span style={{ color: "#7E8299" }}>PAID</span>
              <span
                className="font-weight-bold"
                style={{ color: paidAmount > 0 ? "#15803d" : "#3F4254" }}
              >
                {toPesoAmount(paidAmount, currency).replace(".00", "")}
              </span>
            </div>

            {/* Unpaid */}
            {unpaidAmount > 0 && (
              <div className="d-flex justify-content-between">
                <span style={{ color: "#7E8299" }}>UNPAID</span>
                <span
                  className="font-weight-boldest"
                  style={{
                    color: "#FFA800",
                  }}
                >
                  {toPesoAmount(unpaidAmount, currency).replace(".00", "")}
                </span>
              </div>
            )}
            {/* Add Amount Button */}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default {
  DetailsContainer,
  DietaryRestrictionContainer,
  AdjustmentsContainer,
  ReceiptContainer,
};
